import { ReactComponent as ArrowSvg } from 'images/icons/arrow_select.svg'
import { isNtripConnected } from 'pages/Robot/RobotView/RobotLeftBlock/Service/ntripApplyConfirmation'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectRobot, selectRobotTelemetry } from 'store/selectors/robot'
import { ROBOT_NETWORK_STATUSES } from 'utils/constants'
import { isComboOrOmniRobotType } from 'utils/robotTypeDisplayName'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './RobotAdditionalInfo.module.css'

const cleaningEquipmentStateMapping: Record<number, string> = {
  0: 'MANUAL',
  1: 'TRANSPORT',
  2: 'CLEANING',
  10: 'EMERGENCY',
  255: 'ERROR',
}

const gnssStatusMapping: Record<number, string> = {
  0: 'NO DATA',
  1: 'SINGLE 3D',
  2: 'RTK FLOAT',
  3: 'RTK FIXED',
}

const formatMotorTelemetry = (
  motor: { speed?: number | null; torque?: number | null } | null | undefined,
  labels: { torque: string; speed: string }
): string | null => {
  if (!motor) return null
  if (motor.torque == null && motor.speed == null) return null

  const parts: string[] = []
  if (motor.torque != null) {
    const torquePct = (motor.torque / 10.0).toFixed(1)
    parts.push(`${labels.torque}: ${torquePct}%`)
  }
  if (motor.speed != null) {
    parts.push(`${labels.speed}: ${motor.speed.toFixed(1)} rpm`)
  }
  return parts.length > 0 ? parts.join(', ') : null
}

const RobotAdditionalInfo = () => {
  const { t } = useTranslation()
  const telemetry = useSelector(selectRobotTelemetry)
  const robot = useSelector(selectRobot)
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false)

  let equipmentStateString = '-' // Default if no match
  if (telemetry?.cleaningEquipmentState !== undefined) {
    equipmentStateString = cleaningEquipmentStateMapping[telemetry.cleaningEquipmentState] || 'UNKNOWN'
  }

  const gnssStatus = telemetry?.localization_info?.gnss_status
  let gnssStatusString = '-'
  if (typeof gnssStatus === 'number') {
    gnssStatusString = gnssStatusMapping[gnssStatus] || 'UNKNOWN'
  }

  const gnss1 = telemetry?.localization_info?.gnss1_status
  const hasGnss1 = typeof gnss1 === 'number'
  let gnss1StatusString = '-'
  if (hasGnss1) {
    gnss1StatusString = gnssStatusMapping[gnss1] || 'UNKNOWN'
  }

  const gnss2 = telemetry?.localization_info?.gnss2_status
  const hasGnss2 = typeof gnss2 === 'number'
  let gnss2StatusString = '-'
  if (hasGnss2) {
    gnss2StatusString = gnssStatusMapping[gnss2] || 'UNKNOWN'
  }

  const isOnline = robot.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE
  const isAutonomics = robot.robotInfo?.vendor === VENDOR.AUTONOMICS
  const isComboOrOmniRobot = isComboOrOmniRobotType(robot.robotInfo?.robot_type)

  const motorLabels = { torque: t('robot.motor_torque'), speed: t('robot.motor_speed') }
  const turbineMotorValues = formatMotorTelemetry(telemetry?.vcu?.turbine_motor, motorLabels)
  const scrubberBrushMotorValues = formatMotorTelemetry(telemetry?.vcu?.scrubber_brush_motor, motorLabels)

  if (!isOnline) {
    return null
  }

  return (
    <div className={style.root}>
      <div className={style.additionalInfoHeader}>
        <div>{t('robot.additional_info')}</div>
        <div className={style.showMore} onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}>
          {showAdditionalInfo ? t('robot.additional_info_hide') : t('robot.additional_info_show')}
          <ArrowSvg className={style.icon} />
        </div>
      </div>
      {showAdditionalInfo && (
        <>
          <div className={style.row}>
            <div>{t('robot.localization_status')}</div>
            <div>{telemetry?.localization_info?.localization_status ? 'LOCALIZED' : 'NOT LOCALIZED'}</div>
          </div>
          {isAutonomics &&
            (hasGnss1 || hasGnss2 ? (
              <>
                {hasGnss1 && (
                  <div className={style.row}>
                    <div>{t('robot.gnss1_status')}</div>
                    <div>{gnss1StatusString}</div>
                  </div>
                )}
                {hasGnss2 && (
                  <div className={style.row}>
                    <div>{t('robot.gnss2_status')}</div>
                    <div>{gnss2StatusString}</div>
                  </div>
                )}
              </>
            ) : (
              <div className={style.row}>
                <div>{t('robot.gnss_status')}</div>
                <div>{gnssStatusString}</div>
              </div>
            ))}
          {telemetry?.ntrip != null && (
            <>
              <div className={style.row}>
                <div>{t('robot.ntrip_endpoint')}</div>
                <div>
                  {`${telemetry.ntrip.user || '—'}@${telemetry.ntrip.caster || '—'}:${
                    telemetry.ntrip.port ?? '—'
                  }`}
                </div>
              </div>
              <div className={style.row}>
                <div>{t('robot.ntrip_mountpoint')}</div>
                <div>{telemetry.ntrip.mountpoint || '—'}</div>
              </div>
              <div className={style.row}>
                <div>{t('robot.ntrip_status')}</div>
                <div>{telemetry.ntrip.status || '—'}</div>
              </div>
              {Boolean(telemetry.ntrip.status) &&
                !isNtripConnected(telemetry.ntrip.status) &&
                Boolean(telemetry.ntrip.message) && (
                  <div className={style.row}>
                    <div>{t('robot.ntrip_message')}</div>
                    <div>{telemetry.ntrip.message}</div>
                  </div>
                )}
            </>
          )}
          {telemetry?.localization_info && !telemetry.localization_info.localization_status && (
            <>
              <div className={style.row}>
                <div>{t('robot.imu_status')}</div>
                <div>{telemetry?.localization_info?.imu_bias_status ? 'CONVERGED' : 'NOT CONVERGED'}</div>
              </div>
            </>
          )}
          <div className={style.row}>
            <div>{t('robot.cleaning_equipment_state')}</div>
            <div>{equipmentStateString}</div>
          </div>
          {isAutonomics && turbineMotorValues && (
            <div className={style.row}>
              <div>{t('robot.turbine_motor')}</div>
              <div>{turbineMotorValues}</div>
            </div>
          )}
          {isAutonomics && isComboOrOmniRobot && scrubberBrushMotorValues && (
            <div className={style.row}>
              <div>{t('robot.scrubber_brush_motor')}</div>
              <div>{scrubberBrushMotorValues}</div>
            </div>
          )}
          {/* <div className={style.row}>
            <div>{t('robot.developer_key')}</div>
            <div>{telemetry?.statusCode}</div>
          </div> */}
          {telemetry?.temperatureControlUnit !== null &&
            telemetry?.temperatureControlUnit !== undefined &&
            telemetry.temperatureControlUnit > -273.0 && (
              <div className={style.row}>
                <div>{'Computing unit temperature'}</div>
                <div>{telemetry.temperatureControlUnit.toFixed(1) + '°C'}</div>
              </div>
            )}
          {telemetry?.temperatureChassis !== null &&
            telemetry?.temperatureChassis !== undefined &&
            telemetry.temperatureChassis > -273.0 && (
              <div className={style.row}>
                <div>{'Chassis temperature'}</div>
                <div>{telemetry.temperatureChassis.toFixed(1) + '°C'}</div>
              </div>
            )}
          {telemetry?.temperatureLidar !== null &&
            telemetry?.temperatureLidar !== undefined &&
            telemetry.temperatureLidar > -273.0 && (
              <div className={style.row}>
                <div>{'Lidar temperature'}</div>
                <div>{telemetry.temperatureLidar.toFixed(1) + '°C'}</div>
              </div>
            )}
          {telemetry?.temperatureExternal !== null &&
            telemetry?.temperatureExternal !== undefined &&
            telemetry.temperatureExternal > -273.0 && (
              <div className={style.row}>
                <div>{'External temperature'}</div>
                <div>{telemetry.temperatureExternal.toFixed(1) + '°C'}</div>
              </div>
            )}
          <div className={style.row}>
            <div>{'SIM card number'}</div>
            <div>{robot?.robotInfo?.sim_card_number ? robot.robotInfo.sim_card_number : '-'}</div>
          </div>
        </>
      )}
    </div>
  )
}

export default RobotAdditionalInfo
